import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CompanyService } from '../services/company.service';
import { InvoiceService } from '../services/invoice.service';
import { ProfitReportService } from '../services/profit-report.service';
import { firstValueFrom } from 'rxjs';
import { Firestore, collection, getDocs, query, where } from '@angular/fire/firestore';
import { FeeRateService } from '../services/fee-rate.service';
import { AccountService } from '../services/account.service';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { AccessService } from '../services/access.service';

export type KpiType = 'revenue' | 'fee' | 'expense' | 'collection_rate' | 'profit';

export interface KpiItem {
  id: string;
  name: string;
  value: number;
  trend?: 'up' | 'down' | 'neutral';
  details?: string;
}

@Component({
  selector: 'app-owner-kpi',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './owner-kpi.component.html',
  styleUrls: ['./owner-kpi.component.css']
})
export class OwnerKpiComponent implements OnInit {
  private companyService = inject(CompanyService);
  private invoiceService = inject(InvoiceService);
  private profitReportService = inject(ProfitReportService);
  private feeRateService = inject(FeeRateService);
  private accountService = inject(AccountService);
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private accessService = inject(AccessService);

  years: number[] = [];
  selectedYear: number;
  owners: any[] = [];
  selectedOwnerId: string = '';
  loading: boolean = false;
  
  kpiTypes: { value: KpiType, label: string }[] = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'fee', label: 'Fee' },
    { value: 'expense', label: 'Expense' },
    { value: 'collection_rate', label: 'Invoice Collection Rate' },
    { value: 'profit', label: 'Owner Profit' }
  ];

  allKpiData: { type: KpiType, label: string, items: KpiItem[] }[] = [];

  currentUserProfile: any = null;

  constructor() {
    const currentYear = new Date().getFullYear();
    this.years = [currentYear, currentYear - 1, currentYear - 2];
    this.selectedYear = currentYear;
  }

  ngOnInit() {
    const savedOwnerId = localStorage.getItem('ownerKpi_selectedOwnerId');
    if (savedOwnerId) {
      this.selectedOwnerId = savedOwnerId;
    }
    const savedYear = localStorage.getItem('ownerKpi_selectedYear');
    if (savedYear) {
      const year = parseInt(savedYear, 10);
      if (!isNaN(year) && this.years.includes(year)) {
        this.selectedYear = year;
      }
    }

    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        this.currentUserProfile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
      } else {
        this.currentUserProfile = null;
      }
      this.loadData();
    });
  }

  get isAdminOrSuperAdmin(): boolean {
    return this.currentUserProfile && (this.currentUserProfile.role === 'admin' || this.currentUserProfile.role === 'superadmin');
  }

  onFilterChange() {
    localStorage.setItem('ownerKpi_selectedOwnerId', this.selectedOwnerId);
    localStorage.setItem('ownerKpi_selectedYear', this.selectedYear.toString());
    this.loadData();
  }

  async loadData() {
    this.loading = true;
    let invoices: any[] = [];
    let aggregations: any[] = [];
    let stores: any[] = [];
    let profitData: any[] = [];
    let rates: any[] = [];

    if (this.owners.length === 0) {
      const ownersSnap = await getDocs(collection(this.firestore, 'owners'));
      const allOwners = ownersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const assignedIds = this.currentUserProfile?.ownerIds || [];
      if (assignedIds.length > 0) {
        this.owners = allOwners.filter(o => assignedIds.includes(o.id));
      } else if (this.currentUserProfile && !this.isAdminOrSuperAdmin) {
        this.owners = [];
      } else {
        this.owners = allOwners;
      }
    }

    // If restricted user and no valid owner selected (or "All Owners" selected which is empty string),
    // default to the first assigned owner.
    const assignedIds = this.currentUserProfile?.ownerIds || [];
    if (assignedIds.length > 0 || (this.currentUserProfile && !this.isAdminOrSuperAdmin)) {
      if (!this.selectedOwnerId || !this.owners.find(o => o.id === this.selectedOwnerId)) {
        this.selectedOwnerId = this.owners[0]?.id || '';
      }
    }

    try {
      invoices = await this.invoiceService.getInvoices();
      aggregations = await this.invoiceService.getAggregations('All Periods');
      stores = await firstValueFrom(this.invoiceService.getStores());
      profitData = await this.profitReportService.getProfitDataByYear(this.selectedYear.toString());
      rates = await firstValueFrom(this.feeRateService.getRates());
      const accounts = await firstValueFrom(this.accountService.getAccounts());
      const validCategories = new Set(accounts.map(a => a.name.toLowerCase()));

      const yearStr = this.selectedYear.toString();

      let metrics;

      if (this.selectedOwnerId) {
        const q = query(collection(this.firestore, 'company_ownerships'), where('ownerId', '==', this.selectedOwnerId));
        const ownershipsSnap = await getDocs(q);
        const ownerOwnerships = new Map<string, number>();
        ownershipsSnap.forEach(d => {
          const data = d.data();
          const pct = Number(data['profitSharePercent'] || data['percentage'] || 0);
          const cid = String(data['companyId'] || '').trim();
          if (cid) ownerOwnerships.set(cid, pct);
        });
        
        metrics = this.calculateMetrics(invoices, aggregations, stores, profitData, rates, yearStr, ownerOwnerships, validCategories);
      } else {
        // 1. Calculate Global Metrics (All Owners)
        let ownerOwnerships: Map<string, number> | null = null;
        if (!this.isAdminOrSuperAdmin) {
          ownerOwnerships = new Map<string, number>();
        }
        metrics = this.calculateMetrics(invoices, aggregations, stores, profitData, rates, yearStr, ownerOwnerships, validCategories);
      }

      this.allKpiData = this.kpiTypes.map(kpi => {
        return {
          type: kpi.value,
          label: kpi.label,
          items: this.getAggregatedCompanyData(kpi.value, metrics)
        };
      });
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    }
    this.loading = false;
  }

  private calculateMetrics(invoices: any[], aggregations: any[], stores: any[], profitData: any[], rates: any[], yearStr: string, ownerOwnerships: Map<string, number> | null, validCategories: Set<string>) {
    // Filter data for selected year
    const yearInvoices = invoices.filter(inv => inv.period && inv.period.startsWith(yearStr));

    // Create a set of valid keys based on existing invoices for the year
    // This ensures we only count revenue/fees that were actually invoiced
    const validInvoiceKeys = new Set(yearInvoices.map(inv => `${inv.period}_${String(inv.companyId).trim()}`));

    // Calculate Financials Per Company
    const companyFinancials = new Map<string, { revenue: number, expense: number, fee: number }>();
    
    const getEntry = (cid: string) => {
      if (!companyFinancials.has(cid)) companyFinancials.set(cid, { revenue: 0, expense: 0, fee: 0 });
      return companyFinancials.get(cid)!;
    };

    // 1. Revenue & Fees from Aggregations (Using InvoiceService)
    const revenueFeesMap = this.invoiceService.calculateRevenueAndFees(aggregations, stores, rates, yearStr);

    revenueFeesMap.forEach((val, key) => {
      if (!validInvoiceKeys.has(key)) return;

      // key is period_cid. period is YYYY/MM (7 chars).
      const cid = key.substring(8);
      
      const entry = getEntry(cid);
      entry.revenue += val.revenue;
      entry.fee -= val.fee;
    });

    // 2. Expenses from Profit Report
    const companyExpenses = this.profitReportService.calculateOperatingExpenses(profitData, validCategories);
    
    companyExpenses.forEach((amt, cid) => {
      const entry = getEntry(cid);
      entry.expense += amt;
    });

    // 3. Aggregate Totals

    let totalRevenue = 0;
    let totalFee = 0;
    let totalOpsExpense = 0;
    let profit = 0;

    companyFinancials.forEach((fin, cid) => {
      let ratio = 1;
      if (ownerOwnerships) {
        const share = ownerOwnerships.get(cid);
        ratio = (share !== undefined) ? share / 100 : 0;
      }
      
      totalRevenue += fin.revenue;
      totalFee += Math.abs(fin.fee);
      totalOpsExpense += Math.abs(fin.expense);

      const netIncome = fin.revenue + fin.expense + fin.fee; // expense and fee are negative
      profit += netIncome * ratio;
    });

    const totalExpense = totalOpsExpense; 

    const totalInv = yearInvoices.length;
    const paidInv = yearInvoices.filter(i => i.status === 'Paid').length;
    const collectionRate = totalInv > 0 ? paidInv / totalInv : 0;

    return {
      revenue: totalRevenue,
      fee: totalFee,
      expense: Math.abs(totalExpense),
      collection_rate: collectionRate,
      profit: profit,
      collection_details: `${totalInv} Invoices, ${paidInv} Collected`
    };
  }

  private getAggregatedCompanyData(kpi: KpiType, metrics: any): KpiItem[] {
    const item: KpiItem = {
      id: 'all-companies',
      name: 'All Companies',
      value: metrics[kpi],
      trend: 'neutral'
    };

    if (kpi === 'collection_rate') {
      item.details = metrics.collection_details;
    }

    return [item];
  }
}